import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Chip,
  IconButton,
  Breadcrumbs,
  Link,
  Divider,
  useMediaQuery,
  useTheme,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  RocketLaunch as RocketLaunchIcon,
  Schedule as ScheduleIcon,
  DeviceHub as DeviceHubIcon,
  Block as BlockIcon,
  ContentCopy as ContentCopyIcon,
  History as HistoryIcon,
  Build as BuildIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  helpCategories,
  helpArticles,
  getArticlesByCategory,
  getArticleBySlug,
  getCategoryBySlug,
  searchArticles,
  HelpArticle,
  HelpCategory,
} from './helpArticles';

// Icon mapping for categories
const categoryIcons: Record<string, React.ReactNode> = {
  RocketLaunch: <RocketLaunchIcon />,
  Schedule: <ScheduleIcon />,
  DeviceHub: <DeviceHubIcon />,
  Block: <BlockIcon />,
  ContentCopy: <ContentCopyIcon />,
  History: <HistoryIcon />,
  Build: <BuildIcon />,
};

interface HelpCenterProps {
  onBack?: () => void; // For mobile navigation
}

export const HelpCenter: React.FC<HelpCenterProps> = ({ onBack }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Navigation state
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<{ categorySlug: string; articleSlug: string } | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return searchArticles(searchQuery);
  }, [searchQuery]);

  // Current article
  const currentArticle = useMemo(() => {
    if (!selectedArticle) return null;
    return getArticleBySlug(selectedArticle.categorySlug, selectedArticle.articleSlug);
  }, [selectedArticle]);

  // Current category
  const currentCategory = useMemo(() => {
    if (!selectedCategory) return null;
    return getCategoryBySlug(selectedCategory);
  }, [selectedCategory]);

  // Category articles
  const categoryArticles = useMemo(() => {
    if (!selectedCategory) return [];
    return getArticlesByCategory(selectedCategory);
  }, [selectedCategory]);

  // Handlers
  const handleCategoryClick = (categorySlug: string) => {
    setSelectedCategory(categorySlug);
    setSelectedArticle(null);
    setSearchQuery('');
    setIsSearching(false);
  };

  const handleArticleClick = (article: HelpArticle) => {
    setSelectedArticle({ categorySlug: article.categorySlug, articleSlug: article.slug });
    setSearchQuery('');
    setIsSearching(false);
  };

  const handleBack = () => {
    if (selectedArticle) {
      setSelectedArticle(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    } else if (onBack) {
      onBack();
    }
  };

  const handleSearchFocus = () => {
    setIsSearching(true);
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    setIsSearching(false);
  };

  const handleSearchResultClick = (article: HelpArticle) => {
    setSelectedCategory(article.categorySlug);
    setSelectedArticle({ categorySlug: article.categorySlug, articleSlug: article.slug });
    setSearchQuery('');
    setIsSearching(false);
  };

  // Render article content with markdown
  const renderArticleContent = (article: HelpArticle) => (
    <Box sx={{ pb: 4 }}>
      {/* Back button for mobile */}
      {isMobile && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleBack} edge="start" sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            {article.category}
          </Typography>
        </Box>
      )}

      {/* Breadcrumbs for desktop */}
      {!isMobile && (
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              setSelectedCategory(null);
              setSelectedArticle(null);
            }}
            underline="hover"
            color="inherit"
          >
            Help Center
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => setSelectedArticle(null)}
            underline="hover"
            color="inherit"
          >
            {article.category}
          </Link>
          <Typography variant="body2" color="text.primary">
            {article.title}
          </Typography>
        </Breadcrumbs>
      )}

      <Typography variant="h5" fontWeight={600} gutterBottom>
        {article.title}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {article.summary}
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {/* Markdown content */}
      <Box
        sx={{
          '& h1': { typography: 'h5', fontWeight: 600, mb: 2, mt: 3 },
          '& h2': { typography: 'h6', fontWeight: 600, mb: 1.5, mt: 2.5 },
          '& h3': { typography: 'subtitle1', fontWeight: 600, mb: 1, mt: 2 },
          '& p': { typography: 'body2', mb: 2, lineHeight: 1.7 },
          '& ul, & ol': { pl: 3, mb: 2 },
          '& li': { typography: 'body2', mb: 0.5 },
          '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
          '& th, & td': {
            border: '1px solid',
            borderColor: 'divider',
            p: 1,
            typography: 'body2',
          },
          '& th': { bgcolor: 'grey.100', fontWeight: 600 },
          '& strong': { fontWeight: 600 },
          '& code': {
            bgcolor: 'grey.100',
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            fontFamily: 'monospace',
            fontSize: '0.85em',
          },
          '& blockquote': {
            borderLeft: 3,
            borderColor: 'primary.main',
            pl: 2,
            ml: 0,
            my: 2,
            color: 'text.secondary',
          },
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.content}</ReactMarkdown>
      </Box>

      {/* Tags */}
      <Box sx={{ mt: 4, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {article.tags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            variant="outlined"
            sx={{ textTransform: 'capitalize' }}
          />
        ))}
      </Box>

      {/* Related articles */}
      {article.relatedArticles && article.relatedArticles.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Related Articles
          </Typography>
          <List dense disablePadding>
            {article.relatedArticles.map((relatedId) => {
              const related = helpArticles.find((a) => a.id === relatedId);
              if (!related) return null;
              return (
                <ListItem key={relatedId} disablePadding>
                  <ListItemButton
                    onClick={() => handleArticleClick(related)}
                    sx={{ borderRadius: 1, py: 0.5 }}
                  >
                    <ListItemText
                      primary={related.title}
                      primaryTypographyProps={{ variant: 'body2', color: 'primary.main' }}
                    />
                    <ChevronRightIcon fontSize="small" color="action" />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}
    </Box>
  );

  // Render category view
  const renderCategoryView = (category: HelpCategory) => (
    <Box>
      {/* Back button for mobile */}
      {isMobile && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleBack} edge="start" sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={600}>
            {category.title}
          </Typography>
        </Box>
      )}

      {!isMobile && (
        <>
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => setSelectedCategory(null)}
              underline="hover"
              color="inherit"
            >
              Help Center
            </Link>
            <Typography variant="body2" color="text.primary">
              {category.title}
            </Typography>
          </Breadcrumbs>

          <Typography variant="h5" fontWeight={600} gutterBottom>
            {category.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {category.description}
          </Typography>
        </>
      )}

      <List disablePadding>
        {categoryArticles.map((article, index) => (
          <React.Fragment key={article.id}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleArticleClick(article)}
                sx={{ borderRadius: 1, py: 1.5 }}
              >
                <ListItemText
                  primary={article.title}
                  secondary={article.summary}
                  primaryTypographyProps={{ fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'body2', sx: { mt: 0.5 } }}
                />
                <ChevronRightIcon color="action" />
              </ListItemButton>
            </ListItem>
            {index < categoryArticles.length - 1 && <Divider sx={{ my: 0.5 }} />}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  // Render category list (home view)
  const renderCategoryList = () => (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        {isMobile && onBack && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={onBack} edge="start" sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" fontWeight={600}>
              Help Center
            </Typography>
          </Box>
        )}
        {!isMobile && (
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Help Center
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          Find answers to common questions about Store Control
        </Typography>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search help articles..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onFocus={handleSearchFocus}
        size="small"
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleSearchClear}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Search results */}
      {isSearching && searchQuery && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
          </Typography>
          {searchResults.length > 0 ? (
            <Paper variant="outlined">
              <List disablePadding>
                {searchResults.slice(0, 10).map((article, index) => (
                  <React.Fragment key={article.id}>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => handleSearchResultClick(article)}>
                        <ListItemText
                          primary={article.title}
                          secondary={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip label={article.category} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {article.summary.slice(0, 60)}...
                              </Typography>
                            </Box>
                          }
                          primaryTypographyProps={{ fontWeight: 500, variant: 'body2' }}
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < searchResults.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No articles found. Try different keywords.
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* Categories */}
      {!isSearching && (
        <>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Browse by Topic
          </Typography>

          {isMobile ? (
            // Mobile: Accordion style
            <Box>
              {helpCategories
                .sort((a, b) => a.order - b.order)
                .map((category) => (
                  <Accordion
                    key={category.id}
                    disableGutters
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      mb: 1,
                      borderRadius: '8px !important',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': { mb: 1 },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ minHeight: 56 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ color: 'primary.main' }}>
                          {categoryIcons[category.icon]}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {category.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getArticlesByCategory(category.slug).length} articles
                          </Typography>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <List dense disablePadding>
                        {getArticlesByCategory(category.slug).map((article) => (
                          <ListItem key={article.id} disablePadding>
                            <ListItemButton
                              onClick={() => handleArticleClick(article)}
                              sx={{ borderRadius: 1, py: 0.75 }}
                            >
                              <ListItemText
                                primary={article.title}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                              <ChevronRightIcon fontSize="small" color="action" />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}
            </Box>
          ) : (
            // Desktop: Card grid
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 2,
              }}
            >
              {helpCategories
                .sort((a, b) => a.order - b.order)
                .map((category) => (
                  <Paper
                    key={category.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'rgba(102, 44, 139, 0.04)',
                      },
                    }}
                    onClick={() => handleCategoryClick(category.slug)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          bgcolor: 'rgba(102, 44, 139, 0.1)',
                          color: 'primary.main',
                        }}
                      >
                        {categoryIcons[category.icon]}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {category.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {category.description}
                        </Typography>
                        <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: 'block' }}>
                          {getArticlesByCategory(category.slug).length} articles
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
            </Box>
          )}

                  </>
      )}
    </Box>
  );

  // Main render
  return (
    <Box
      sx={{
        height: '100%',
        bgcolor: isMobile ? '#f8f9fa' : 'transparent',
        overflow: 'auto',
      }}
    >
      <Box
        sx={{
          maxWidth: 800,
          mx: 'auto',
          p: isMobile ? 2 : 3,
          pb: isMobile ? 8 : 3, // Extra padding for mobile bottom nav
        }}
      >
        {currentArticle ? (
          renderArticleContent(currentArticle)
        ) : currentCategory ? (
          renderCategoryView(currentCategory)
        ) : (
          renderCategoryList()
        )}
      </Box>
    </Box>
  );
};

export default HelpCenter;
